import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { useAuth } from '@/contexts/AuthContext';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { sessionConfig, idleTimeoutMs } from '@/config/session';
import { ClockIcon } from '@/icons';

/**
 * Session/idle-timeout controller + warning dialog. Mounted inside the
 * authenticated shell. While active it silently keeps the token fresh; on
 * prolonged inactivity it warns with a countdown, then signs the user out.
 */
export function SessionTimeoutModal() {
  const { status, logout, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(Math.floor(sessionConfig.warnBeforeMs / 1000));
  const countdown = useRef<ReturnType<typeof setInterval> | null>(null);

  // 30 minutes, or 12 hours when the user chose "Remember me" (shared by all tabs).
  const idleMs = useMemo(() => idleTimeoutMs(), [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const { reset } = useIdleTimeout({
    idleMs,
    warnBeforeMs: sessionConfig.warnBeforeMs,
    enabled: status === 'authenticated',
    onWarn: () => setWarning(true),
    onActive: () => setWarning(false),
    onTimeout: async () => {
      setWarning(false);
      await logout('idle');
      navigate('/login', { replace: true });
    },
  });

  // Countdown while the warning is visible.
  useEffect(() => {
    if (!warning) { if (countdown.current) clearInterval(countdown.current); return; }
    setRemaining(Math.floor(sessionConfig.warnBeforeMs / 1000));
    countdown.current = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => { if (countdown.current) clearInterval(countdown.current); };
  }, [warning]);

  // Silent keep-alive refresh on an interval while authenticated.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const id = setInterval(() => { refreshSession().catch(() => { /* interceptor handles loss */ }); }, sessionConfig.keepAliveMs);
    return () => clearInterval(id);
  }, [status, refreshSession]);

  const stay = async () => {
    setWarning(false);
    try { await refreshSession(); } catch { /* interceptor handles */ }
    reset();
  };

  const signOutNow = async () => {
    setWarning(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <Modal isOpen={warning} onClose={stay} size="sm" isDismissable={false} hideCloseButton backdrop="blur">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 text-fg">
          <ClockIcon className="h-5 w-5 text-warning" /> Still there?
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-fg-muted">
            You'll be signed out in <span className="font-mono font-semibold text-fg">{mm}:{ss}</span> due to inactivity.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={signOutNow}>Sign out</Button>
          <Button color="primary" onPress={stay}>Stay signed in</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
